import React, {useState} from "react";
import Spacer from "../../shared/tds/components/@atoms/spacer";
import Spacings from "../../shared/tds/components/tokens/layout/spacings";
import InputField from "../input-field";
import Button from "../button";
import DropdownField from "../dropdown-field";
import useStates from "../../hooks/use-states";
import useCountries from "../../hooks/use-countries";
import useSubmitPersonalInformationRequest from "../../hooks/use-submit-personal-information-request";
import useVinDecoder from "../../hooks/use-vin-decoder";

const MyPersonalInformationContainer: React.FC = () => {
  const [requestType, setRequestType] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [vinNumber, setVinNumber] = useState("");
  const vinDecoder = useVinDecoder();

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitPersonalInformationRequest = useSubmitPersonalInformationRequest((data) => {
    setSubmitting(false);

    if (data?.submitPersonalInformationRequest) {
      if (data.submitPersonalInformationRequest.statusCode == 200) {
        setSuccessMessage(data.submitPersonalInformationRequest.message);
        setRequestType('');
        setFirstName('');
        setLastName('');
        setAddress1('');
        setAddress2('');
        setCountry('');
        setCity('');
        setState('');
        setZipCode('');
        setEmail('');
        setConfirmEmail('');
        setPhone('');
        setVinNumber('');
      } else {
        setErrorMessage(data?.submitPersonalInformationRequest.message);
      }
    }
  });

  const requestOptions = [
    { label: '- Choose One -', value: '' },
    { label: 'Access my personal information', value: 'Access my personal information' },
    { label: 'Correct Inaccurate personal information', value: 'Correct Inaccurate personal information' },
    { label: 'Delete some or all of my personal information', value: 'Delete some or all of my personal information' },
    { label: 'Do not share or sell my personal information', value: 'Do not share or sell my personal information' },
  ];

  const states = useStates();
  const countries = useCountries();

  const mapCountryToOption = (country: { name: any; code: any; }) => {
    return {
      label: country.name,
      value: country.code,
    };
  };

  const mapStateToOption = (state: { name: any; stateCode: any; }) => {
    return {
      label: state.name,
      value: state.stateCode,
    };
  };

  const renderStateField = () => {
    const stateOptions = states
      .filter((s) => s.countryCode === country)
      .map(mapStateToOption);

    return stateOptions.length ? (
      <DropdownField
        id="state"
        label="State"
        value={state}
        options={[{ label: '- Choose One -', value: '' }, ...stateOptions]}
        required={true}
        onChange={(e) => setState(e.target.value)}
      />
    ) : (
      <InputField
        id="state"
        label="State"
        type="text"
        defaultValue={state}
        required={true}
        onChange={(e) => setState(e.target.value)}
      />
    );
  };

  const countryOptions = countries
    .filter(
      (c: { code: string; }) =>
        c.code === 'US' ||
        c.code === 'CA' ||
        c.code === 'PR' ||
        c.code === 'SE'
    )
    .map(mapCountryToOption);

  const onSubmit = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setSubmitting(true);

    if (email !== confirmEmail) {
      setErrorMessage("Email doesn't match confirm email");
      setSubmitting(false);
      return;
    }

    // validate VIN number
    if (vinNumber) {
      if (
        !(
          vinNumber.toLowerCase().startsWith('yv1') ||
          vinNumber.toLowerCase().startsWith('yv4') ||
          vinNumber.toLowerCase().startsWith('lvy') ||
          vinNumber.toLowerCase().startsWith('7jr') ||
          vinNumber.toLowerCase().startsWith('lyv')
        )
      ) {
        setErrorMessage('This is not a Volvo VIN Number');
        setSubmitting(false);
        return;
      }

      const data = await vinDecoder(vinNumber as string);

      if (data.errors.length) {
        setErrorMessage(data.errors.join(', '));
        setSubmitting(false);
        return;
      }
    }

    submitPersonalInformationRequest({
      requestType,
      firstName,
      lastName,
      address1,
      address2,
      country,
      city,
      state,
      zipCode,
      email,
      phone,
      vinNumber,
    })
  };

  return <>
    <h2>My Personal Information</h2>
    <p>We respect all our customers and we apply that same respect to the personal information they give us when
      purchasing, subscribing to, or inquiring about our products & services.</p>
    <p>This Personal Information Privacy Rights Request Form allows you to exercise certain privacy rights. By
      submitting this form, you can exercise your right to access, correct, delete, and restrict the use or sale of your
      personal information (subject to exemptions). If you live in a state that gives residents enhanced privacy rights
      – such as the California Privacy Rights Act (CPRA) – we will respond to all requests as required by law. If you
      live in a state that does not give you enhanced privacy rights, we will respond to all requests at our earliest
      opportunity.</p>
    <Spacer spacing={Spacings.medium} orientation="vertical"/>
    <DropdownField
      id="request"
      label="Type of Request"
      value={requestType}
      options={requestOptions}
      onChange={(e) => setRequestType(e.target.value)}
    />
    <InputField
      id="firstName"
      label="First Name"
      type="text"
      required={true}
      defaultValue={firstName}
      onChange={(e) => setFirstName(e.target.value)}
    />
    <InputField
      id="lastName"
      label="Last Name"
      type="text"
      required={true}
      defaultValue={lastName}
      onChange={(e) => setLastName(e.target.value)}
    />
    <InputField
      id="address1"
      label="Address 1"
      type="text"
      required={true}
      defaultValue={address1}
      onChange={(e) => setAddress1(e.target.value)}
    />
    <InputField
      id="address2"
      label="Address 2"
      type="text"
      defaultValue={address2}
      onChange={(e) => setAddress2(e.target.value)}
    />
    <DropdownField
      id="country"
      label="Country"
      value={country}
      options={[{ label: '- Choose One -', value: '' }, ...countryOptions]}
      required={true}
      onChange={(e) => setCountry(e.target.value)}
    />
    <InputField
      id="city"
      label="City"
      type="text"
      required={true}
      defaultValue={city}
      onChange={(e) => setCity(e.target.value)}
    />
    {renderStateField()}
    <InputField
      id="zipCode"
      label="Zip Code"
      type="text"
      required={true}
      defaultValue={zipCode}
      onChange={(e) => setZipCode(e.target.value)}
    />
    <InputField
      id="email"
      label="Email"
      type="text"
      required={true}
      defaultValue={email}
      onChange={(e) => setEmail(e.target.value)}
    />
    <InputField
      id="confirmEmail"
      label="Confirm Email"
      type="text"
      required={true}
      defaultValue={confirmEmail}
      onChange={(e) => setConfirmEmail(e.target.value)}
    />
    <InputField
      id="phone"
      label="Phone"
      type="text"
      required={true}
      defaultValue={phone}
      onChange={(e) => setPhone(e.target.value)}
    />
    <InputField
      id="vinNumber"
      label="VIN Number"
      type="text"
      defaultValue={vinNumber}
      onChange={(e) => setVinNumber(e.target.value)}
    />
    {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
    {successMessage && <p>{successMessage}</p>}
    {requestType &&
      firstName &&
      lastName &&
      address1 &&
      country &&
      city &&
      state &&
      zipCode &&
      email &&
      confirmEmail &&
      phone &&
      <>
        {submitting
          ? <p>Submitting...</p>
          : <Button text="Submit" onClick={onSubmit}/>
        }
      </>
    }
  </>
}

export default MyPersonalInformationContainer;
